import * as React from 'react'

import {
  FormControl,
  FormDescription,
  FormFieldContext,
  FormItem,
  FormLabel,
  useFormField
} from '@/components/ui/form'
import { cn } from '@/lib/utils'

/**
 * Campo de formulário composto por slots.
 *
 * O primitivo genérico vem de `@/components/ui/form` (registry shadcn), que já
 * resolve `useId`, `htmlFor`, `aria-describedby` e `aria-invalid`. Esta camada
 * aplica os tokens do design system e os pisos de `ACCESSIBILITY.md` por cima —
 * a divisão dual-layer descrita no MASTER §6.
 *
 * A API é de composição, não de configuração (`compositionStyle: compound`), o
 * que permite rótulo com marcação própria sem virar mais uma prop:
 *
 *     <Field name="login">
 *       <Field.Label>E-mail</Field.Label>
 *       <Field.Control>
 *         <TextField autoComplete="username" />
 *       </Field.Control>
 *       <Field.Description>Enviamos o código para este endereço.</Field.Description>
 *       <Field.Error />
 *     </Field>
 *
 * O controle interno se registra sozinho no `react-hook-form` lendo o `name` do
 * contexto via `useFormField`. `Field` não monta um `Controller` para não
 * disputar o registro com o controle.
 */

type FieldProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Caminho do campo no formulário, como em `register`/`getFieldState`. */
  name: string
}

const FieldRoot = React.forwardRef<HTMLDivElement, FieldProps>(
  ({ name, className, ...props }, ref) => (
    <FormFieldContext.Provider value={{ name }}>
      <FormItem ref={ref} className={cn('space-y-1.5', className)} {...props} />
    </FormFieldContext.Provider>
  )
)
FieldRoot.displayName = 'Field'

/**
 * Rótulo. 15px por MASTER §3 — o valor é arbitrário porque a escala nomeada não
 * tem um degrau em 15px; o MASTER prescreve `text-[0.9375rem]` explicitamente.
 */
const FieldLabel = React.forwardRef<
  React.ElementRef<typeof FormLabel>,
  React.ComponentPropsWithoutRef<typeof FormLabel>
>(({ className, ...props }, ref) => {
  // `FormLabel` aplica a cor de erro antes do `className` recebido, e o
  // tailwind-merge faz o último vencer — uma cor fixa aqui anularia o estado
  // de erro. Por isso a cor é resolvida a partir do estado real do campo.
  const { error } = useFormField()

  return (
    <FormLabel
      ref={ref}
      className={cn(
        'block text-[0.9375rem] font-semibold',
        error ? 'text-danger-text' : 'text-gray-900',
        className
      )}
      {...props}
    />
  )
})
FieldLabel.displayName = 'Field.Label'

/** Slot do controle — injeta id e os atributos ARIA no filho. */
const FieldControl = FormControl

/**
 * Texto de apoio. `text-gray-700` porque `ACCESSIBILITY.md` proíbe `-400`,
 * `-500` e `-600` para texto secundário.
 */
const FieldDescription = React.forwardRef<
  React.ElementRef<typeof FormDescription>,
  React.ComponentPropsWithoutRef<typeof FormDescription>
>(({ className, ...props }, ref) => (
  <FormDescription
    ref={ref}
    className={cn('text-sm text-gray-700', className)}
    {...props}
  />
))
FieldDescription.displayName = 'Field.Description'

/**
 * Mensagem de erro. `text-danger-text`, não `text-danger` — a variante crua dá
 * 3.40:1 e reprova como texto (ACCESSIBILITY.md §1.3). `role="alert"` para o
 * erro ser anunciado quando aparece, além de já estar referenciado pelo
 * controle via `aria-describedby`.
 */
const FieldError = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField()

  // O `FormMessage` do registry faz `String(error.message)`. Isso quebra a
  // mensagem que não é texto puro — e o app tem várias, como o
  // "E-mail não encontrado, clique aqui para criar uma conta", em que o
  // "clique aqui" é um elemento clicável. Com `String`, o usuário leria
  // "[object Object]".
  const message = error?.message as React.ReactNode
  const body = message ?? children

  if (!body) return null

  return (
    <p
      ref={ref}
      id={formMessageId}
      role="alert"
      className={cn('text-sm font-medium text-danger-text', className)}
      {...props}
    >
      {body}
    </p>
  )
})
FieldError.displayName = 'Field.Error'

export const Field = Object.assign(FieldRoot, {
  Label: FieldLabel,
  Control: FieldControl,
  Description: FieldDescription,
  Error: FieldError
})

export { useFormField }
